import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface BreadcrumbSegment {
  label: string;
  path?: string | null;
  icon?: React.ReactNode;
}

interface BreadcrumbContextValue {
  segments: BreadcrumbSegment[];
  setSegments: (segments: BreadcrumbSegment[]) => void;
  pushSegment: (segment: BreadcrumbSegment) => void;
  popSegment: () => void;
  clearSegments: () => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [segments, setSegmentsState] = useState<BreadcrumbSegment[]>([]);

  const setSegments = useCallback((newSegments: BreadcrumbSegment[]) => {
    setSegmentsState(newSegments);
  }, []);

  const pushSegment = useCallback((segment: BreadcrumbSegment) => {
    setSegmentsState(prev => [...prev, segment]);
  }, []);

  const popSegment = useCallback(() => {
    setSegmentsState(prev => prev.slice(0, -1));
  }, []);

  const clearSegments = useCallback(() => {
    setSegmentsState([]);
  }, []);

  return (
    <BreadcrumbContext.Provider value={{ segments, setSegments, pushSegment, popSegment, clearSegments }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumbConfig(): BreadcrumbSegment[] {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    return [];
  }
  return context.segments;
}

export function useBreadcrumb() {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumb must be used within a BreadcrumbProvider');
  }
  return context;
}

/**
 * Non-throwing variant for leaf views (e.g. ledger show pages) that set
 * breadcrumb segments when mounted inside the layout and no-op when rendered
 * outside a provider (unit tests).
 */
export function useBreadcrumbSafe() {
  return useContext(BreadcrumbContext);
}